import React from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { deleteExtraInfo } from '@/utils/layer-kit';
import {
    LayerType,
    MIN_IMG_SIZE,
    type IActiveBaseInfo,
    type IExportBehavior,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type ISize,
} from '@/version-types';
import { EdgelessQRCodeLayer } from './edgeless-layer';
import { renderLayerContentToCanvas } from './render/render-to-canvas';
import { renderLayerToPPT } from './render/render-to-ppt';
import { renderLayerToStage } from './render/render-to-stage';

function getBehavior(identity: string): ILayerBehavior {
    return {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessQRCodeLayer(layer.id, getFramework(identity));
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            deleteExtraInfo(layer);
        },

        async getResultCanvasAsync(layer: ILayerProps, stageSize: ISize): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, stageSize);
        },

        async getLayerImageAsync(layer: ILayerProps, globalAlpha = true) {
            return renderLayerContentToCanvas(layer, globalAlpha);
        },

        async savePPTLayerAsync(slide, layer) {
            return renderLayerToPPT(slide, layer);
        },

        whenGroupCanResize(layer: ILayerProps) {
            return {
                horizontal: true,
                vertical: true,
            };
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            return {
                cutWidth: MIN_IMG_SIZE,
                cutHeight: MIN_IMG_SIZE,
            };
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;

            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.qrCode);
        },
        getMenuOverlapIcon(layer: ILayerProps) {
            if (layer.width <= 0 || layer.height <= 0) {
                return <IconFont name="QrCodeBold" fontSize={16} />;
            }

            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            return (
                <ResourceThumbnail resource={resource} fallbackIcon={<IconFont name="QrCodeBold" fontSize={16} />} />
            );
        },
    };
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.QR_CODE,
    getBehavior,
};

export default exportBehavior;
